import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar, Legend
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, AlertTriangle, DollarSign,
  RefreshCw, Calendar, ChevronRight, Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SpendTrends() {
  const [weeklyTrends, setWeeklyTrends] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [vendorCosts, setVendorCosts] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trends');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [trendsRes, forecastRes, vendorRes, compRes] = await Promise.all([
        axios.get(`${API}/trends/weekly`, { withCredentials: true }),
        axios.get(`${API}/forecast`, { withCredentials: true }),
        axios.get(`${API}/trends/vendor-costs`, { withCredentials: true }),
        axios.get(`${API}/comparison`, { withCredentials: true })
      ]);
      setWeeklyTrends(trendsRes.data);
      setForecast(forecastRes.data);
      setVendorCosts(vendorRes.data);
      setComparison(compRes.data);
    } catch (error) {
      console.error('Failed to fetch trends data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-rose-500" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-emerald-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
        {[
          { id: 'trends', label: 'Weekly Trends' },
          { id: 'forecast', label: 'Forecast' },
          { id: 'vendors', label: 'Vendor Costs' },
          { id: 'comparison', label: 'Period Compare' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === tab.id
                ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
            data-testid={`trends-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Weekly Trends Tab */}
      {activeTab === 'trends' && weeklyTrends && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Trend Summary */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 dark:text-white">Spending Trend</h3>
              <div className="flex items-center gap-2">
                {getTrendIcon(weeklyTrends.trend_direction)}
                <span className={`text-sm font-medium ${
                  weeklyTrends.trend_percentage > 0 ? 'text-rose-600' : 
                  weeklyTrends.trend_percentage < 0 ? 'text-emerald-600' : 'text-slate-500'
                }`}>
                  {weeklyTrends.trend_percentage > 0 ? '+' : ''}{weeklyTrends.trend_percentage}%
                </span>
              </div>
            </div>
            
            {weeklyTrends.weeks.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={weeklyTrends.weeks}>
                  <defs>
                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4338CA" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#4338CA" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="week_start" 
                    tick={{ fontSize: 11 }} 
                    stroke="#64748B"
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#64748B" tickFormatter={(v) => `$${v}`} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Spent']}
                    labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString()}`}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total_amount" 
                    stroke="#4338CA" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorAmount)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Not enough data for trends
              </div>
            )}
          </div>

          {/* Weekly Breakdown */}
          {weeklyTrends.weeks.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <h4 className="font-medium text-slate-900 dark:text-white mb-3">Recent Weeks</h4>
              <div className="space-y-2">
                {weeklyTrends.weeks.slice(-4).reverse().map((week, idx) => (
                  <div key={week.week_start} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Week of {new Date(week.week_start).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-500">{week.invoice_count} invoices</p>
                      </div>
                    </div>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      ${week.total_amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Forecast Tab */}
      {activeTab === 'forecast' && forecast && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {forecast.message ? (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 text-center">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 dark:text-slate-400">{forecast.message}</p>
            </div>
          ) : (
            <>
              {/* Forecast Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Avg Monthly Spend</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    ${forecast.average_monthly_spend.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {getTrendIcon(forecast.trend === 'increasing' ? 'up' : forecast.trend === 'decreasing' ? 'down' : 'stable')}
                    <span className="text-xs text-slate-500 capitalize">{forecast.trend}</span>
                  </div>
                </div>
                
                {forecast.monthly_budget && (
                  <div className={`border rounded-xl p-4 ${
                    forecast.budget_at_risk 
                      ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-700' 
                      : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700'
                  }`}>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Budget Status</p>
                    <p className={`text-xl font-bold ${
                      forecast.budget_at_risk ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'
                    }`}>
                      {forecast.budget_at_risk ? 'At Risk' : 'On Track'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Budget: ${forecast.monthly_budget.toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* Forecast Chart */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-4">3-Month Forecast</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={[...forecast.historical_months, ...forecast.forecast_months.map(f => ({ ...f, isForecast: true }))]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748B" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#64748B" tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      formatter={(value, name) => [`$${value.toLocaleString()}`, name === 'forecast_amount' ? 'Forecast' : 'Actual']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      name="Actual"
                      stroke="#4338CA" 
                      strokeWidth={2}
                      dot={{ fill: '#4338CA' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="forecast_amount" 
                      name="Forecast"
                      stroke="#7C3AED" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#7C3AED' }}
                    />
                    {forecast.monthly_budget && (
                      <Line 
                        type="monotone" 
                        dataKey={() => forecast.monthly_budget} 
                        name="Budget"
                        stroke="#EF4444" 
                        strokeWidth={1}
                        strokeDasharray="3 3"
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Forecast Details */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 dark:text-white mb-3">Predicted Spending</h4>
                <div className="space-y-2">
                  {forecast.forecast_months.map((month) => (
                    <div key={month.month} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{month.month}</p>
                        <p className="text-xs text-slate-500 capitalize">Confidence: {month.confidence}</p>
                      </div>
                      <span className={`font-semibold ${
                        forecast.monthly_budget && month.forecast_amount > forecast.monthly_budget
                          ? 'text-rose-600' : 'text-slate-900 dark:text-white'
                      }`}>
                        ${month.forecast_amount.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* Vendor Costs Tab */}
      {activeTab === 'vendors' && vendorCosts && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Alerts Summary */}
          {vendorCosts.alerts_count > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <p className="font-medium text-amber-900 dark:text-amber-300">
                  {vendorCosts.alerts_count} vendor(s) with significant price increases
                </p>
              </div>
            </div>
          )}

          {/* Vendor List */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Vendor Price Trends</h3>
            
            {vendorCosts.vendors.length > 0 ? (
              <div className="space-y-3">
                {vendorCosts.vendors.slice(0, 10).map((vendor) => (
                  <div 
                    key={vendor.vendor_name}
                    className={`p-3 rounded-lg border ${
                      vendor.alert 
                        ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-700' 
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 dark:text-white">{vendor.vendor_name}</p>
                          {vendor.alert && (
                            <span className="px-1.5 py-0.5 text-xs bg-rose-100 dark:bg-rose-800 text-rose-700 dark:text-rose-300 rounded">
                              Alert
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{vendor.invoice_count} invoices</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          {getTrendIcon(vendor.trend === 'increasing' ? 'up' : vendor.trend === 'decreasing' ? 'down' : 'stable')}
                          <span className={`text-sm font-medium ${
                            vendor.change_percentage > 0 ? 'text-rose-600' : 
                            vendor.change_percentage < 0 ? 'text-emerald-600' : 'text-slate-500'
                          }`}>
                            {vendor.change_percentage > 0 ? '+' : ''}{vendor.change_percentage}%
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Avg: ${vendor.average_amount.toLocaleString()} → Latest: ${vendor.latest_amount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                Not enough vendor data for analysis
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Period Comparison Tab */}
      {activeTab === 'comparison' && comparison && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Comparison Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{comparison.current_month.name}</p>
              <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                ${comparison.current_month.total.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">{comparison.current_month.count} invoices</p>
            </div>
            
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{comparison.previous_month.name}</p>
              <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">
                ${comparison.previous_month.total.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">{comparison.previous_month.count} invoices</p>
            </div>
          </div>

          {/* Change Indicator */}
          <div className={`rounded-xl p-6 text-center ${
            comparison.change_direction === 'up' 
              ? 'bg-rose-50 dark:bg-rose-900/20' 
              : comparison.change_direction === 'down'
              ? 'bg-emerald-50 dark:bg-emerald-900/20'
              : 'bg-slate-50 dark:bg-slate-800'
          }`}>
            <div className="flex items-center justify-center gap-2 mb-2">
              {comparison.change_direction === 'up' && <TrendingUp className="w-8 h-8 text-rose-500" />}
              {comparison.change_direction === 'down' && <TrendingDown className="w-8 h-8 text-emerald-500" />}
              {comparison.change_direction === 'same' && <Minus className="w-8 h-8 text-slate-400" />}
            </div>
            <p className={`text-3xl font-bold ${
              comparison.change_direction === 'up' ? 'text-rose-700 dark:text-rose-400' :
              comparison.change_direction === 'down' ? 'text-emerald-700 dark:text-emerald-400' :
              'text-slate-700 dark:text-slate-300'
            }`}>
              {comparison.change_percentage > 0 ? '+' : ''}{comparison.change_percentage}%
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              {comparison.change_direction === 'up' ? 'Increased from' :
               comparison.change_direction === 'down' ? 'Decreased from' :
               'Same as'} last month
            </p>
          </div>

          {/* Bar Comparison */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <h4 className="font-medium text-slate-900 dark:text-white mb-4">Visual Comparison</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { name: comparison.previous_month.name, amount: comparison.previous_month.total },
                { name: comparison.current_month.name, amount: comparison.current_month.total }
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" tickFormatter={(v) => `$${v}`} />
                <Tooltip 
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Total']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                />
                <Bar dataKey="amount" fill="#4338CA" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default SpendTrends;
